import { useApi } from '@src/app/context/Api';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';

import fields from './includes/fields.json';

const { note } = fields;

/**
 * Button: EditNote
 *
 * Displays a button that triggers a modal popup to edit notes for the action path.
 *
 * @param actionPath
 * @returns {Element}
 * @constructor
 */
export function EditNote({ actionPath = {} }) {
  const [showModal, setShowModal] = useState(false);

  const { toast } = useToast();

  const { clearApiCache, useApiMutation } = useApi('ActionPath');

  const [SaveActionPathNote] = useApiMutation('SaveActionPathNote');

  const defaultValues = {
    note: actionPath?.note,
  };

  const form = useForm({ defaultValues });

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    form?.reset(defaultValues, {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: false,
      keepIsValid: false,
      keepSubmitCount: true,
      keepTouched: false,
      keepValues: false,
    });

    return setShowModal(false);
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    await handleClose();

    const toastId = 'actionPath-notes-add';
    toast.show({
      ariaLoadingLabel: 'Saving Notes to Action Path',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Action Path is preparing to save the notes.',
      title: 'Saving Notes to Action Path...',
      variant: 'info',
    });

    const payload = {
      actionPathId: actionPath?.id,
      note: submittedValues?.note,
    };

    await SaveActionPathNote(payload, {
      onError: (error) => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: 'Unable to save notes.',
          title: 'Saving Notes Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'ActionPath', queryKeys: ['GetActionPath'] }]);
        toast.hide(`${toastId}-info`);

        toast.show({
          id: `${toastId}-success`,
          message: 'Notes have been saved.',
          title: 'Saved Notes to Action Path',
          variant: 'success',
        });
      },
    });

    form?.reset(defaultValues, {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: false,
      keepIsValid: false,
      keepSubmitCount: true,
      keepTouched: false,
      keepValues: false,
    });
  };

  return (
    <ErrorHandler location="src/features/ActionPaths/EditNote/EditNote.jsx">
      <User.Capability attributes={['write:note']} resource="ActionPaths">
        <Button
          after={<IconSymbol icon="edit" size="20" variant="outlined" />}
          onClick={() => {
            return setShowModal(true);
          }}
          variant="solid"
        >
          Edit Note
        </Button>
      </User.Capability>

      <User.Capability attributes={['write:note']} method="hide" resource="ActionPaths">
        <Modal isOpen={showModal} onClose={handleClose} title="Edit Note">
          <FormProvider autoComplete="off" highlighted onSubmit={handleSubmit} state={form}>
            <Modal.Section>
              <Grid>
                <Grid.Col
                  span={{
                    xs: '100%',
                  }}
                >
                  <TextInputArea {...note} />
                </Grid.Col>
              </Grid>
            </Modal.Section>
            <Modal.Section>
              <Divider height={1} />
              <Layout.Group alignLayout="right">
                <Button onClick={handleClose} variant="outline">
                  Cancel
                </Button>
                <Button isDisabled={isEmpty(form?.getValues('note'))} type="submit" variant="solid">
                  Edit
                </Button>
              </Layout.Group>
            </Modal.Section>
          </FormProvider>
        </Modal>
      </User.Capability>
    </ErrorHandler>
  );
}
